import React, { useEffect, useState } from "react";
import api from "../../api/axios";
import {
  Wifi,
  UserCheck,
  UserX,
  Plane,
  TrendingDown,
  ChevronsRight,
  TrendingUp,
  ChartPie,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import AttendanceChart from "./Components/AttendanceChart";
import AttendanceBarChart from "./Components/AttendanceBarChart";
import EmployeeReport from "./Components/EmployeeReport";
import LoadingScreen from "../../Components/LoadingScreen-inner";

const HRMSDashboard = () => {
  const navigate = useNavigate();

  const [stats, setStats] = useState({
    totalEmployees: 0,
    present: 0,
    absent: 0,
    leave: 0,
    late: 0,
    early: 0,
    outdoor: 0,
    missed: 0,
    activeDevices: 0,
    offlineDevices: [],
  });

  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  const [report, setReport] = useState({ absentTop10: [], lateTop10: [] });
  const [reportLoading, setReportLoading] = useState(false);
  const [loadedMonth, setLoadedMonth] = useState(null);
  const extractMonthKey = (dateString) => dateString.slice(0, 7);

  const [attendanceHistory, setAttendanceHistory] = useState([]);
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarTitle, setSidebarTitle] = useState("");
  const [sidebarData, setSidebarData] = useState([]);

  function parseLateMinutes(lateString) {
    if (!lateString) return 0;
    const hMatch = lateString.match(/(\d+)h/);
    const mMatch = lateString.match(/(\d+)m/);
    return (
      (hMatch ? parseInt(hMatch[1]) : 0) * 60 +
      (mMatch ? parseInt(mMatch[1]) : 0)
    );
  }

  // ======= CLICK HANDLERS =======
  const handleNavigation = (filter) => {
    navigate(`/attendance?filter=${filter}&date=${selectedDate}`);
  };

  const openSidebar = (type) => {
    let filtered = [];

    if (type === "late") {
      filtered = attendanceRows.filter(
        (r) => parseLateMinutes(r.late_minutes) > 0
      );
      setSidebarTitle("Late Coming Employees");
    }

    if (type === "early") {
      filtered = attendanceRows.filter(
        (r) => parseLateMinutes(r.early_minutes) > 0
      );
      setSidebarTitle("Early Going Employees");
    }

    setSidebarData(filtered);
    setSidebarOpen(true);
    console.log(filtered);
  };

  // ======= MONTHLY REPORT RESTORED =======
  const fetchMonthlyAttendance = async (data) => {
    const today = new Date(data);

    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
      .toISOString()
      .split("T")[0];
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)
      .toISOString()
      .split("T")[0];

    const res = await api.get("/hrms/attendance", {
      params: {
        type: "range",
        from_date: firstDay,
        to_date: today.toISOString().split("T")[0],
        limit: 20000,
        page: 1,
      },
    });

    const rows = res.data.data || [];

    const absent = {};
    const late = {};

    rows.forEach((r) => {
      const key = r.employee_code || r.code;
      if (!key) return;

      if (r.status === "Absent") {
        absent[key] = absent[key] || { ...r, total: 0 };
        absent[key].total++;
      }

      if (parseLateMinutes(r.late_minutes) > 0) {
        late[key] = late[key] || { ...r, total: 0 };
        late[key].total++;
      }
    });

    return {
      absentTop10: Object.values(absent)
        .sort((a, b) => b.total - a.total)
        .slice(0, 10),
      lateTop10: Object.values(late)
        .sort((a, b) => b.total - a.total)
        .slice(0, 10),
    };
  };

  // ======= WEEKLY HISTORY =======
  const generateWeeklyHistory = async (currentDate) => {
    const end = new Date(currentDate);
    const start = new Date(currentDate);
    start.setDate(start.getDate() - 6);

    const from_date = start.toISOString().split("T")[0];
    const to_date = end.toISOString().split("T")[0];

    const res = await api.get("/hrms/attendance", {
      params: { type: "range", from_date, to_date, limit: 10000, page: 1 },
    });

    const rows = res.data.data || [];
    const result = [];
    const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

    const grouped = rows.reduce((acc, row) => {
      if (!acc[row.date]) acc[row.date] = [];
      acc[row.date].push(row);
      return acc;
    }, {});

    Object.keys(grouped).forEach((date) => {
      const dateObj = new Date(date);
      const dayName = days[dateObj.getDay()];
      const rows = grouped[date];

      const present = rows.filter(
        (r) => r.status === "Present" || parseLateMinutes(r.late_minutes) > 0
      ).length;

      const late = rows.filter(
        (r) => parseLateMinutes(r.late_minutes) > 0
      ).length;

      result.push({ name: dayName, onTime: present - late, late });
    });

    setAttendanceHistory(result);

    // 🔥 Monthly API only when month changes
    const selectedMonth = extractMonthKey(currentDate);

    if (loadedMonth !== selectedMonth) {
      setReportLoading(true);
      const monthlyResult = await fetchMonthlyAttendance(currentDate);
      setReport(monthlyResult);
      setLoadedMonth(selectedMonth);
      setReportLoading(false);
    }
  };

  // ======= DASHBOARD FETCH =======
  const fetchDashboardData = async (date) => {
    try {
      setLoading(true);

      const attendanceRes = await api.get("/hrms/attendance", {
        params: { type: "daily", date, limit: 10000, page: 1 },
      });

      const rows = attendanceRes.data.data || [];
      const totalCount = attendanceRes.data.total || rows.length;
      setAttendanceRows(rows);

      let deviceRows = [];
      try {
        const devRes = await api.get("/hrms/devices");
        deviceRows = devRes.data.data || [];
      } catch {}

      setStats({
        totalEmployees: totalCount,
        present: rows.filter(
          (r) => r.status === "Present" || parseLateMinutes(r.late_minutes) > 0
        ).length,
        absent: rows.filter((r) => r.status === "Absent").length,
        late: rows.filter((r) => parseLateMinutes(r.late_minutes) > 0).length,
        early: rows.filter((r) => parseLateMinutes(r.early_minutes) > 0).length,
        leave: rows.filter((r) => r.status === "Leave").length,
        activeDevices:
          deviceRows.filter((d) => d.status === "online").length || 0,
        offlineDevices: deviceRows.filter((d) => d.status === "offline") || [],
      });

      generateWeeklyHistory(date);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData(selectedDate);
  }, [selectedDate]);

  const dashCards = [
    {
      title: "Active Devices",
      count: stats.activeDevices,
      icon: Wifi,
      icon_color: "#06b6d4",
      bgColor: "#ecfeff",
    },
    {
      title: "Present Employees",
      count: stats.present,
      icon: UserCheck,
      icon_color: "#22c55e",
      bgColor: "#f0fdf4",
      onClick: () => handleNavigation("present"),
    },
    {
      title: "Absent Employees",
      count: stats.absent,
      icon: UserX,
      icon_color: "#ef4444",
      bgColor: "#fef2f2",
      onClick: () => handleNavigation("absent"),
    },
    {
      title: "On Leave",
      count: stats.leave,
      icon: Plane,
      icon_color: "#eab308",
      bgColor: "#fefce8",
    },
    {
      title: "Late Coming",
      count: stats.late,
      icon: TrendingDown,
      icon_color: "#f97316",
      bgColor: "#fff7ed",
      onClick: () => openSidebar("late"),
    },
    {
      title: "Early Going",
      count: stats.early,
      icon: ChevronsRight,
      icon_color: "#14b8a6",
      bgColor: "#f0fdfa",
      onClick: () => openSidebar("early"),
    },
    {
      title: "Total Employees",
      count: stats.totalEmployees,
      icon: TrendingUp,
      icon_color: "#6366f1",
      bgColor: "#eef2ff",
    },
  ];

  return (
    <>
      {/* ========= YOUR ORIGINAL UI 100% UNCHANGED ========= */}
      <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
        <div
          className="d-flex flex-wrap justify-content-between align-items-center mb-4 p-3 bg-white rounded shadow-sm"
          style={{ border: "1px solid #e5e7eb" }}
        >
          <div>
            <h4 className="fw-semibold mb-1">Attendance Dashboard</h4>
            <p className="text-muted mb-0">
              Summary as of <strong>{selectedDate}</strong>
            </p>
          </div>

          <div className="d-flex align-items-center gap-2">
            <label className="fw-medium text-secondary mb-0">
              Select Date:
            </label>
            <input
              type="date"
              className="form-control form-control-sm"
              style={{ width: "180px" }}
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
            />
          </div>
        </div>
      </div>

      {loading ? (
        // <div className="text-center py-5">
        //   <div className="spinner-border text-primary"></div>
        // </div>
        <LoadingScreen />
      ) : (
        <>
          <div className="px-xl-5 px-lg-4 px-3 py-3 page-body">
            <div className="row">
              <div className="col-12">
                <div className="hrms-dash-main-align mt-2">
                  {/* Cards */}
                  <div className="row g-2 row-cols-lg-4 row-cols-md-3 row-cols-sm-2">
                    {dashCards.map((item, i) => (
                      <div className="col" key={i}>
                        <div
                          className="hrms-dashcard"
                          style={{
                            backgroundColor: item.bgColor,
                            cursor: item.onClick ? "pointer" : "default",
                          }}
                          onClick={item.onClick}
                        >
                          <div className="hrms-dashcard-body">
                            <div className="d-flex justify-content-between align-items-center">
                              <h5 className="fs-6 hrms-dashcard-title m-0">
                                {item.title}
                              </h5>
                              <item.icon
                                style={{ color: item.icon_color }}
                                className="card-icon"
                              />
                            </div>
                            <p className="hrms-dashcard-count m-0">
                              {item.count}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="row g-3 mb-4">
                    {/* Present / Absent Card */}
                    <div className="col-lg-4 col-md-6">
                      <div className="hrms-dashcard2 p-3 shadow-sm rounded bg-white h-100">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <ChartPie style={{ color: "#302867" }} />
                          <h5 className="m-0 fw-semibold">
                            Employee Present/Absent
                          </h5>
                        </div>

                        <span className="text-muted small d-block mb-3">
                          Employee presence for {selectedDate}.
                        </span>

                        <div className="d-flex flex-column justify-content-center align-items-center py-3">
                          <div className="text-dark fw-semibold fs-2">
                            {stats.totalEmployees > 0
                              ? Math.round(
                                  (stats.present / stats.totalEmployees) * 100
                                )
                              : 0}
                            %
                          </div>
                          <div className="text-muted">Present</div>
                        </div>

                        <AttendanceChart
                          present={stats.present}
                          absent={stats.absent}
                        />
                      </div>
                    </div>

                    {/* Offline Devices Card */}
                    <div className="col-lg-8 col-md-6">
                      <div className="hrms-dashcard2 p-3 shadow-sm rounded bg-white h-100 d-flex flex-column">
                        <div className="d-flex align-items-center gap-2 mb-2">
                          <TrendingUp style={{ color: "#302867" }} />
                          <h5 className="m-0 fw-semibold">Offline Devices</h5>
                        </div>

                        <div className="table-responsive mt-2 flex-grow-1">
                          <table className="table table-bordered small mb-0 align-middle">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>Serial</th>
                                <th>Last Ping</th>
                              </tr>
                            </thead>

                            <tbody>
                              {stats.offlineDevices.length > 0 ? (
                                stats.offlineDevices.map((device) => (
                                  <tr key={device.id || device.serial_number}>
                                    <td>{device.device_name || "-"}</td>
                                    <td>{device.serial_number || "-"}</td>
                                    <td className="text-nowrap">
                                      {device.last_ping_at
                                        ? new Date(
                                            device.last_ping_at
                                          ).toLocaleString()
                                        : "N/A"}
                                    </td>
                                  </tr>
                                ))
                              ) : (
                                <tr>
                                  <td
                                    colSpan={3}
                                    className="text-center text-muted py-3"
                                  >
                                    All devices are online 🚀
                                  </td>
                                </tr>
                              )}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Your charts + EmployeeReport remain unchanged */}
                  <div className="dashcard3-container mb-4">
                    <div className="d-flex flex-column gap-2 mb-2">
                      <h5 className="m-0 fw-semibold">Attendance Overview</h5>
                      <span className="text-muted small">
                        On-time vs. late entries for the last 7 days.
                      </span>
                    </div>
                    <AttendanceBarChart data={attendanceHistory} />
                  </div>

                  <EmployeeReport
                    absentTop10={report.absentTop10}
                    lateTop10={report.lateTop10}
                    loading={reportLoading}
                  />
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ========= SIDEBAR (new) — UI unchanged ========= */}
      <div
        className={`offcanvas offcanvas-end ${sidebarOpen ? "show" : ""}`}
        style={{
          visibility: sidebarOpen ? "visible" : "hidden",
          width: "850px",
        }}
      >
        <div className="offcanvas-header">
          <h5 className="offcanvas-title">{sidebarTitle}</h5>
          <button
            type="button"
            className="btn-close"
            onClick={() => setSidebarOpen(false)}
          ></button>
        </div>

        <div className="offcanvas-body p-3">
          <div className="border rounded-3 overflow-hidden">
            <div className="table-responsive">
              <table className="table table-sm mb-0 align-middle">
                <thead className="table-light">
                  <tr>
                    <th className="ps-3">Employee</th>
                    <th>Code</th>
                    <th>Dept</th>
                    <th className="text-end pe-3">
                      {sidebarTitle === "Late Coming Employees"
                        ? "Late By"
                        : "Early Going"}
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {sidebarData.map((r, i) => (
                    <tr key={i}>
                      <td className="ps-3 fw-medium">{r.name}</td>
                      <td>{r.employee_code}</td>
                      <td>{r.department}</td>
                      <td className="text-end pe-3">
                        {sidebarTitle === "Late Coming Employees"
                          ? r.late_minutes
                          : r.early_minutes}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HRMSDashboard;
